import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getBindingGraph } from "../helpers/utils";
import { useSharedProps } from "./SharedProps";

/**
 * Project-scoped in-memory index of the full app↔service binding graph.
 *
 * Fetches ONCE per project mount (backed by coord PR #231's
 * GET /api/v1/binding-graph — a single joined response covering both
 * directions of the graph). Every dashboard reads from this cache via
 * two lookup helpers:
 *
 *   bindingsForApp(appName)        -> [{kind, displayName, k8sName}]
 *   bindingsForInstance(path, k8s) -> [{name}]  (bound apps)
 *
 * Both are O(1) hashmap reads. No per-card fetches on poll ticks.
 *
 * Invalidation:
 *   - refresh()                  — full re-fetch (Refresh button, etc.)
 *   - invalidateAfterMutation() — same as refresh(), called by helpers
 *                                  after any bind/unbind/create/delete
 *
 * By design we do NOT poll — the graph is small (~5 KB at 10-15 apps ×
 * ~5 kinds) and mutations always go through the console's action code
 * paths, which call invalidateAfterMutation() explicitly. If we ever
 * add out-of-band mutations (e.g. via CLI while the browser tab is
 * open) a soft-poll or WebSocket signal can be added here without
 * touching any consumer.
 */

const BindingGraphContext = createContext({
  loading: false,
  ready: false,
  error: null,
  bindingsForApp: () => [],
  bindingsForInstance: () => [],
  totalBindings: 0,
  refresh: () => Promise.resolve(),
  invalidateAfterMutation: () => Promise.resolve(),
});

export const BindingGraphProvider = ({ children }) => {
  const { currentGroup } = useSharedProps();
  const projectId = currentGroup?.id;

  const [state, setState] = useState({
    loading: false,
    ready: false,
    error: null,
    appIndex: new Map(),      // appName -> [KnServiceBoundServiceRecord]
    instanceIndex: new Map(), // `${path}/${k8sName}` -> [{name}]
    totalBindings: 0,
  });

  // Track which project the current index is for so a project switch
  // is a clean cache invalidation, not a stale render.
  const currentIndexProjectRef = useRef(null);

  const refresh = useCallback(() => {
    if (!projectId) {
      setState((prev) => ({
        ...prev,
        loading: false,
        ready: true,
        error: null,
        appIndex: new Map(),
        instanceIndex: new Map(),
        totalBindings: 0,
      }));
      currentIndexProjectRef.current = null;
      return Promise.resolve();
    }
    const projectAtStart = projectId;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    return new Promise((resolve) => {
      getBindingGraph((result, err) => {
        // Guard against a project switch that happens mid-flight.
        if (currentIndexProjectRef.current === projectAtStart && err) {
          // no-op: we still return the last known good index below
        }
        if (err || !result) {
          setState((prev) => ({
            ...prev,
            loading: false,
            ready: true,
            error: err || "Failed to load binding graph",
          }));
          resolve(null);
          return;
        }
        if (projectId !== projectAtStart) {
          // Project changed while the request was in flight — discard.
          resolve(null);
          return;
        }
        const appIndex = new Map();
        for (const entry of result.apps || []) {
          if (!entry?.appName) continue;
          appIndex.set(entry.appName, Array.isArray(entry.bindings) ? entry.bindings : []);
        }
        const instanceIndex = new Map();
        let total = 0;
        for (const entry of result.instances || []) {
          if (!entry?.path || !entry?.k8sName) continue;
          const bound = Array.isArray(entry.boundApps) ? entry.boundApps : [];
          instanceIndex.set(`${entry.path}/${entry.k8sName}`, bound);
          total += bound.length;
        }
        currentIndexProjectRef.current = projectAtStart;
        setState({
          loading: false,
          ready: true,
          error: null,
          appIndex,
          instanceIndex,
          totalBindings: total,
        });
        resolve(result);
      });
    });
  }, [projectId]);

  // Fetch on mount + every time the project switches. Consumers that
  // want a fresh copy can call refresh() or invalidateAfterMutation().
  useEffect(() => {
    refresh();
  }, [refresh]);

  const bindingsForApp = useCallback(
    (appName) => (appName && state.appIndex.get(appName)) || [],
    [state.appIndex]
  );
  const bindingsForInstance = useCallback(
    (path, k8sName) =>
      (path && k8sName && state.instanceIndex.get(`${path}/${k8sName}`)) || [],
    [state.instanceIndex]
  );

  const value = useMemo(
    () => ({
      loading: state.loading,
      ready: state.ready,
      error: state.error,
      totalBindings: state.totalBindings,
      bindingsForApp,
      bindingsForInstance,
      refresh,
      invalidateAfterMutation: refresh,
    }),
    [
      state.loading,
      state.ready,
      state.error,
      state.totalBindings,
      bindingsForApp,
      bindingsForInstance,
      refresh,
    ]
  );

  return (
    <BindingGraphContext.Provider value={value}>
      {children}
    </BindingGraphContext.Provider>
  );
};

export const useBindingGraph = () => useContext(BindingGraphContext);
